import { action, computed, makeObservable, observable, override } from 'mobx'

import ModalViewModel from 'infra/ModalViewModel'
import { format } from 'date-fns'
import { Nullable } from 'types/definitions'
import sortBy from 'lodash/sortBy'
import groupBy from 'lodash/groupBy'
import map from 'lodash/map'
import { TimeRange } from 'jobs/TimeRange'
import { task } from 'mobx-task'
import { DeferredPromise } from 'infra/deferred'
import Consultation from '../../../jobs/consultations/Consultation'
import Member from '../../../iam/models/Member'
import User from '../../../iam/models/User'

/**
 * A day and its timeslots for which the pro can book a consultation
 * based on the client's availability.
 */
interface ScheduledDay {
  formattedDay: string
  timeSlots: TimeRange[]
}

/**
 * Information used for creating a consultation and prompting the user.
 */
export interface ConsultationDetails {
  requestedLength: number
  availability: TimeRange[] | undefined
  bookedConsultations: Consultation[]
  client: User
}

/**
 * Consultation details.
 */
const ConsultationDetails: { none: ConsultationDetails } = {
  /**
   * An empty details "instance".
   */
  none: {
    requestedLength: 30,
    availability: [],
    bookedConsultations: [],
    client: User.none,
  },
}

/**
 * View Model for the Consultation Modal.
 */
export default class ConsultationModalViewModel extends ModalViewModel {
  /**
   * The time slot selected by the pro. This is one of the time slots
   * allowable based on the customer's specified availability.
   */
  @observable
  selectedTimeSlot: Nullable<TimeRange> = null

  /**
   * The days and the time slots within them for which the pro can book
   * the consultation based on the client's availability.
   */
  @observable
  availableDays: ScheduledDay[] = []

  /**
   * Details for the consultation.
   */
  @observable private _consultationDetails: ConsultationDetails =
    ConsultationDetails.none

  /**
   * The current member (likely the pro picking up the job).
   */
  _currentMember: Member = Member.none

  /**
   * The provider's selection. This promise can be awaited by whichever caller
   * inflated the modal to receive the pro's selected time slot.
   */
  deferredSelectedTimeSlot = new DeferredPromise<TimeRange>()

  /**
   * Constructor.
   */
  constructor() {
    super()

    makeObservable(this)
  }

  /**
   * The customer's requested length in minutes for the Consultation.
   */
  @computed
  get requestedLength() {
    return this._consultationDetails.requestedLength
  }

  /**
   * The customer's availability for the Consultation.
   */
  @computed
  get availability() {
    return this._consultationDetails.availability
  }

  /**
   * List of booked consultations.
   */
  @computed
  get bookedConsultations() {
    return this._consultationDetails.bookedConsultations
  }

  /**
   * The client member.
   */
  @computed
  get client() {
    return this._consultationDetails.client
  }

  /**
   * The current member (likely the pro picking up the job).
   */
  @computed
  get currentMember() {
    return this._currentMember
  }

  /**
   * Inflates the Modal.
   */
  @action
  promptForTimeSlots(
    consultationDetails: ConsultationDetails,
    currentMember: Member,
  ) {
    this._consultationDetails = consultationDetails
    this._currentMember = currentMember

    this.show()

    this.deferredSelectedTimeSlot.reset()
    this.availableDays = []
    this.availableDays = this.computeScheduledDays(this.availability ?? [])

    return this.deferredSelectedTimeSlot.promise
  }

  /**
   * @inheritDoc
   */
  @override
  async close() {
    this.deferredSelectedTimeSlot.resolve(null)
    this.showing = false
  }

  /**
   * Selects the time slot.
   */
  @action
  selectTimeSlot(range: TimeRange) {
    this.selectedTimeSlot = range
  }

  /**
   * Given the customer's availability,
   */
  private computeScheduledDays(ranges: TimeRange[]) {
    const sorted = sortBy(ranges, 'startTime')
    const grouped = groupBy(sorted, (t) => format(t.startTime, 'EEEE, d MMMM'))

    return map(grouped, (timeSlots, formattedDay) => {
      return { formattedDay, timeSlots }
    })
  }

  /**
   * Submits the selected time slot.
   */
  submit = task.resolved(async () => {
    if (!this.selectedTimeSlot) {
      return
    }

    this.deferredSelectedTimeSlot.resolve(this.selectedTimeSlot)
    this.close()
  })
}
